import { Platform } from 'react-native';

/** A file on the device, as the pickers and the offline queue describe it. */
export interface NativeFileSource {
	uri: string;
	name: string;
	type: string;
}

/**
 * A multipart file part that expo/fetch can encode.
 *
 * SDK 58 installs expo/fetch as the global `fetch` on iOS/Android. Its FormData encoder
 * (`expo/build/winter/fetch/convertFormData`) accepts strings, Blobs and objects with `bytes()` —
 * React Native's `{ uri, name, type }` descriptor throws "Unsupported FormDataPart implementation"
 * before the request is sent. The encoder takes the filename from `name` and the content type from
 * `type`, so those carry the caller's values rather than whatever the on-disk file is called.
 */
export interface NativeFormFile {
	name: string;
	type: string;
	bytes: () => Promise<Uint8Array>;
}

/** Wraps a device file so expo/fetch reads its bytes when it encodes the request. */
export const nativeFormFile = ({ uri, name, type }: NativeFileSource): NativeFormFile => ({
	name,
	type,
	bytes: async () => {
		// Imported on use: the native module does not exist on web or in jsdom specs.
		const { File } = await import('expo-file-system');
		return new File(uri).bytes();
	},
});

/** Appends a device file to `formData` in the shape expo/fetch encodes. Native only. */
export const appendNativeFile = (formData: FormData, field: string, source: NativeFileSource): void => {
	// `FormData.append` is typed for DOM Blobs. Expo's FormData patch stores any object as-is and the
	// encoder reads it through `bytes()`, so there is no typed path for this value.
	formData.append(field, nativeFormFile(source) as unknown as Blob);
};

/** What expo-document-picker hands back for one file. */
export interface PickedDocument {
	uri: string;
	name: string;
	mimeType?: string | null;
}

/**
 * A picked document as a `/files` upload body: `file`, `fileName`, then `fields` in order. Web reads
 * the picker's URI into a browser File; native sends the file from disk as a bytes-backed part.
 */
export const pickedDocumentFormData = async (
	asset: PickedDocument,
	fields: Record<string, string>,
): Promise<FormData> => {
	if (Platform.OS !== 'web') {
		const type = asset.mimeType || 'application/octet-stream';
		return nativeUploadFormData({ uri: asset.uri, name: asset.name, type }, { fileName: asset.name, ...fields });
	}
	const blob = await fetch(asset.uri).then((response) => response.blob());
	const formData = new FormData();
	formData.append('file', new File([blob], asset.name, { type: asset.mimeType || blob.type }));
	formData.append('fileName', asset.name);
	for (const [key, value] of Object.entries(fields)) formData.append(key, value);
	return formData;
};

/**
 * A native `/files` upload body: the `file` part first, then `fields` in order. Undefined fields are
 * left out; empty strings are sent, as the callers' previous FormData builders did.
 */
export const nativeUploadFormData = (file: NativeFileSource, fields: Record<string, string | undefined>): FormData => {
	const formData = new FormData();
	appendNativeFile(formData, 'file', file);
	for (const [key, value] of Object.entries(fields)) {
		if (value !== undefined) formData.append(key, value);
	}
	return formData;
};
